/**
 * src/utils/useReleaseFlag.js
 *
 * useReleaseFlag(key) — is this project's module released to the signed-in
 * tenant? (RPDA-04). One shared read of release-flags-read per page load;
 * the answer is the SERVER's resolution (RFLG-B-002/005), never one the
 * browser computes from state it could have been handed.
 *
 * Fail closed: while loading, when the read fails, and for any key the
 * server did not resolve ON, the answer is false — the module stays hidden.
 *
 * Visibility only. A hidden module's records are still counted by every
 * evaluator, obligation, task and finding (RFLG-B-003), and a release never
 * overrides plan gating: callers AND this with the plan gate (RFLG-B-004).
 *
 * Use it to hide a nav item (navConfig `releaseFlag: '<key>'`, honoured by
 * navRoleAllowed) and to guard a route (<ReleaseFlagGate flag="<key>">).
 */

import { useCallback, useEffect, useState } from 'react';
import { getReleaseFlags } from '../api/releaseFlagsApi.js';
import { RELEASE_FLAGS, releasedKeys } from './releaseFlags.js';

const EMPTY = new Set();
const FAILED = { released: EMPTY, failed: true };
let cached = null;

/**
 * { released, failed } — `failed` when the read failed or the server said some
 * flag that is not ON could not be read (`incomplete`), so no caller tells a
 * user a module is switched off when that is unknown.
 */
function readReleased() {
  // Nothing registered ⇒ nothing can be released: no request at all.
  if (RELEASE_FLAGS.length === 0) return Promise.resolve({ released: EMPTY, failed: false });
  if (!cached) {
    cached = getReleaseFlags()
      .then((res) => ({ released: releasedKeys(res), failed: !!(res && res.incomplete === true) }))
      .catch(() => {
        cached = null; // retry on the next mount; hidden meanwhile
        return FAILED;
      });
  }
  return cached;
}

/** Test seam: forget the shared read. */
export function resetReleaseFlagCache() {
  cached = null;
}

/**
 * { loaded, released, failed, retry } — `released` is the set of keys the server
 * resolved ON (empty while loading and on failure); `loaded` lets a route guard
 * render nothing rather than a refusal while the read is in flight; `failed`
 * says an answer that is not ON is unknown rather than OFF: a surface holding
 * records a tenant must act on shows a retry there instead of hiding silently
 * (RFLG-B-006). `retry` re-reads.
 */
export function useReleaseFlagState({ enabled = true } = {}) {
  const [state, setState] = useState({ loaded: !enabled, released: EMPTY, failed: false });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    readReleased().then(({ released, failed }) => {
      if (live) setState({ loaded: true, released, failed });
    });
    return () => {
      live = false;
    };
  }, [enabled, attempt]);
  const retry = useCallback(() => {
    setState((prev) => ({ ...prev, loaded: false, failed: false }));
    setAttempt((n) => n + 1);
  }, []);
  return { ...state, retry };
}

/**
 * { on, loaded, failed, retry } for one key — for a surface that holds a
 * tenant's records, which must not read a failed read as "nothing here".
 */
export function useReleaseFlagStatus(key) {
  const { loaded, released, failed, retry } = useReleaseFlagState();
  return { on: released.has(key), loaded, failed, retry };
}

/** The set of keys released to this tenant (empty while loading or on failure). */
export function useReleasedFlags(opts) {
  return useReleaseFlagState(opts).released;
}

/** true only when the server resolved `key` ON for this tenant. */
export function useReleaseFlag(key) {
  return useReleasedFlags().has(key);
}
